import * as React from 'react';
import * as styled from './styled';
import {getImageByPreview} from 'api/library';
import {getUserByID} from 'api/user/requests';
import {UserObject} from 'stores/UserStore';
import useLoading from 'hooks/use-loading';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import Button, {ButtonVariant} from 'components/Button';
import {IconChat} from 'icons';
import {AuthorCardProps} from './types';

/**
 * The card with information about author.
 * @param {AuthorCardProps} props
 * @return {React.ReactElement<AuthorCardProps>}
 * @constructor
 */
export default function AuthorCard(
    props: AuthorCardProps,
): React.ReactElement<AuthorCardProps> {
  const {
    data, fetch, render,
  } = useLoading<UserObject>();

  React.useEffect(() => {
    fetch(() => new Promise((resolve) => (
      getUserByID(props.userId).then((response) => {
        validateResponse(response);
        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )))
  }, [props.userId]);

  /**
   * Render avatar.
   * @return {JSX.Element}
   */
  function renderAvatar(): JSX.Element {
    return (
      <styled.Avatar
        src={getImageByPreview(data?.avatar)}
        alt="Изображение автора"
      />
    );
  }

  /**
   * Render name.
   * @return {JSX.Element}
   */
  function renderName(): JSX.Element {
    return (
      <styled.Name>
        {data?.name}
      </styled.Name>
    );
  }

  /**
   * Render role.
   * @return {JSX.Element}
   */
  function renderRole(): JSX.Element {
    return (
      <styled.Role>
        Куратор
      </styled.Role>
    );
  }

  /**
   * Render contact button.
   * @return {JSX.Element}
   */
  function renderContactButton(): JSX.Element {
    return (
      <styled.Button>
        <Button type="button" variant={ButtonVariant.Outline}>
          <IconChat /> Написать
        </Button>
      </styled.Button>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    return (
      <React.Fragment>
        {renderAvatar()}
        {renderName()}
        {renderRole()}
        {renderContactButton()}
      </React.Fragment>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <span>Пользователь не найден.</span>
    );
  }

  return (
    <styled.Wrapper>
      {render(renderContent, renderNotFound)}
    </styled.Wrapper>
  );
}
